import type { Booking } from "../types";
import { narrowBookingFromApi, narrowBookingsFromApiList } from "./bookingRowGuard";

/** Shape returned by Netlify bookings action update after zero-waste snapshot. */
export type BookingsUpdateApiResult = {
  ok?: boolean;
  booking?: unknown;
  removedId?: string;
  bookings?: unknown[];
};

function sortBookingsDesc(rows: Booking[]): Booking[] {
  return [...rows].sort((a, b) => (b.rawDate || "").localeCompare(a.rawDate || ""));
}

/**
 * Apply server update response to admin/global booking list (no extra list fetch).
 * Falls back to clientMergedRow when the API only returns { ok: true } (legacy).
 */
export function mergeGlobalBookingsFromUpdateResponse(
  prev: Booking[],
  updatedId: string,
  res: BookingsUpdateApiResult,
  clientMergedRow: Booking | null,
): Booking[] {
  if (res.removedId && Array.isArray(res.bookings) && res.bookings.length > 0) {
    const byId = new Map(prev.map((x) => [x.id, x]));
    byId.delete(res.removedId);
    const merged = narrowBookingsFromApiList(res.bookings);
    for (const b of merged) {
      byId.set(b.id, b);
    }
    return sortBookingsDesc(Array.from(byId.values()));
  }

  if (res.booking !== undefined && res.booking !== null) {
    const row = narrowBookingFromApi(res.booking);
    if (row) {
      const byId = new Map(prev.map((x) => [x.id, x]));
      byId.set(row.id, row);
      return sortBookingsDesc(Array.from(byId.values()));
    }
  }

  if (clientMergedRow) {
    return sortBookingsDesc(prev.map((b) => (b.id === updatedId ? clientMergedRow : b)));
  }

  return prev;
}

/** Merge API add / accept rows into the global list (admin calendar). */
export function mergeUpsertManyIntoGlobalBookings(prev: Booking[], rows: readonly unknown[]): Booking[] {
  const byId = new Map(prev.map((x) => [x.id, x]));
  for (const b of narrowBookingsFromApiList(rows)) {
    byId.set(b.id, b);
  }
  return sortBookingsDesc(Array.from(byId.values()));
}
